import React, {useState, useContext} from 'react';
import { Link } from "react-router-dom";
import { IconButton, InputAdornment, FormGroup, FormControl, Grid, TextField } from "@material-ui/core";
import { MaskedNumber, FormSelect, Form, SwitchValue, Alert, LabelledPaper, FormCheckbox, FormMemo } from '../../../../controls';
import SystemContext from "../../../../SystemContext";
import TariffChargeCodesValidator from "../../validators/TariffChargeCodesValidator";
import { useUpdateComboValue } from "../../../../services/settings";

const ComboDetailsForm = (_props) => {
  const props = { 
    canEdit: false,   
    ..._props
  }

  const flags = [
    { code:"dangerousGoods", name: "Dangerous Goods"},
    { code:"valuables", name: "Valuables"},
    { code:"express", name: "Express"},
    { code:"hatchingEggs", name: "Hatching Eggs"},
    { code:"humanRemains", name: "Human Remains"},
    { code:"freezerRequired", name: "Freezer Required"},
    { code:"coldStorageRequired", name: "Cold Storage Required"},
    { code:"warmStorageRequired", name: "Warm Storage Required"},
    { code:"radioActive", name: "Radioactive"},
    { code:"cargoAircraftOnly", name: "Cargo Aircraft Only"},
    { code:"eFreight", name: "E Freight"},
    { code:"stackable", name: "Stackable"},
    { code:"tiltable", name: "Tiltable"},
    { code:"crated", name: "Crated"}
  ];

  const defaultRecord = {
    "dangerousGoods":false,
    "valuables":false,
    "express":false,
    "hatchingEggs":false,
    "humanRemains":false,
    "freezerRequired":false,
    "coldStorageRequired":false,
    "warmStorageRequired":false,
    "radioActive":false,
    "cargoAircraftOnly":false,
    "eFreight":false,
    "stackable": false,
    "tiltable": false,
    "crated": false,    
    "dgrClass": "",
  };

  const classes = props.classes;
  const [comboRecord, setComboRecord] = useState(props.comboRecord);
  const [record, setRecord] = useState({...defaultRecord, ...(props.comboRecord?props.comboRecord.data:null)});
  const [editing, setEditing] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });

  if (props.comboRecord && (props.comboRecord.comboValueUuid !== comboRecord.comboValueUuid)) {
    setRecord(props.comboRecord.data);
    setComboRecord(props.comboRecord);
    setEditing(false);
  }

  const SystemData = useContext(SystemContext);

  const handleUpdateComboValue = useUpdateComboValue();

  const extraKeys = [];

  const dgrClassOptions = [
    {"code":"", "value": ""},
    {"code":"1","value":"Class 1 Explosive"},
    {"code":"1.1","value":"Class 1.1 Explosive"},
    {"code":"1.2","value":"Class 1.2 Explosive"},
    {"code":"1.3","value":"Class 1.3 Explosive"},
    {"code":"1.4","value":"Class 1.4 Explosive"},
    {"code":"1.5","value":"Class 1.5 Explosive"},
    {"code":"1.6","value":"Class 1.6 Explosive"},
    {"code":"2.1","value":"Class 2.1 Flammable Gas"},
    {"code":"2.2","value":"Class 2.2 Non-Flammable Gas"},
    {"code":"2.3","value":"Class 2.3 Toxic Gas"},
    {"code":"3","value":"Class 3 Flammable Liquid"},
    {"code":"4.1","value":"Class 4.1 Flammable Solid"},
    {"code":"4.2","value":"Class 4.2 Spontaneously Combustible"},
    {"code":"4.3","value":"Class 4.3 Dangerous When Wet"},
    {"code":"5.1","value":"Class 5.1 Oxidizer"},
    {"code":"5.2","value":"Class 5.2 Organic Peroxides"},
    {"code":"6.1","value":"Class 6.1 Toxic"},
    {"code":"6.2","value":"Class 6.2 Infectious Substance"},
    {"code":"7","value":"Class 7 Radioactive"},
    {"code":"7.1","value":"Class 7 Radioactive I"},
    {"code":"7.2","value":"Class 7 Radioactive II"},
    {"code":"7.3","value":"Class 7 Radioactive III"},
    {"code":"7 F","value":"Class 7 Fissile"},
    {"code":"8","value":"Class 8 Corrosive"},
    {"code":"9","value":"Class 9 Miscellaneous"},
    {"code":"9 L","value":"Class 9 Lithium Batteries"},
    {"code":"9 M","value":"Class 9 Magnetized Material"},
  ];

  return (
    <div key="formContainer" style={{width: "100%", textAlign:"left"}}>
      <Alert
        key="alert"
        onChange={(open) => {
          setAlert({ ...alert, open })
        }}
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        timeOut={alert.timeOut || 0}
      />
      {record ?
      <Form key="form"
        message=""
        open={true}
        record={record}
        validator={editing?TariffChargeCodesValidator:null}
        formBodyStyle={{maxHeight: "calc(100vh - 320px)"}}
        buttonBarStyleOverride={{position: "relative", width: "calc(100% - 30px)", marginTop: -30}}
        buttons = {editing?
          {
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}}, 
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }: props.canEdit?{
            edit: {icon: "edit", key: "Edit", label: "Edit", color: "primary"}
          }: {}
        }
      
        onChange={(state) => {
          if (state.buttonPressed === "edit" && props.canEdit) {
            setEditing(true);
          }
          if (state.buttonPressed === "cancel") {
            setEditing(false);
            setRecord({...defaultRecord, ...(props.comboRecord?props.comboRecord.data:null)});
          }
          if (state.buttonPressed === "save") {
            SystemData.toggleLoadingProgress(true);
            const record = state.record;
            handleUpdateComboValue.mutate({
              extraKeys,
              record: {...comboRecord, data: {...record}}, 
              postSuccess: () => {
                setEditing(false);
                SystemData.toggleLoadingProgress(false);
                setAlert({ severity: "info", open: true, message: "Record Updated", timeOut: 5 });
              },
              postError: (data) => {
                SystemData.toggleLoadingProgress(false);
                if (data.errorMessage) {
                  SystemData.alert("Error", `${data.errorMessage}`);
                } else {
                  SystemData.alert("Error", `An Unexpected Error has occurred creating the trip.`);
                }
              }
            });
          } else {
          }
        }}
      >
        <Grid container checkChildren={true} spacing={3}>
          {flags.map(flag => (
            <Grid item xs={3} checkChildren={true}>
              {(record) => (
                <FormCheckbox 
                  id={flag.code}
                  label={flag.name}
                  key={flag.code}
                  className={classes.checkbox}
                  readOnly={editing!==true}
                />
              )}
            </Grid>              
          ))}          
          <Grid item xs={12} checkChildren={true}>
            <FormSelect 
              id="dgrClass"
              label="Dangerous Goods Class"
              options={dgrClassOptions}
              readOnly={editing!==true}
            />
          </Grid>    
        </Grid>
      </Form>
      :<></>
    }
    </div>
  );
}

export default ComboDetailsForm;